// Libs
import { Box, FormHelperText, InputAdornment, InputLabel, Portal, TextField } from '@mui/material';
import classNames from 'classnames/bind';
import moment from 'moment';
import { forwardRef, InputHTMLAttributes, memo, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import HeaderDatePicker from './headerDatePicker/HeaderDatePicker';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { DateFormatEnum } from '~/utils/enum/common';
// Styles, images, icons
import { icons } from '~/assets';
import styles from './CommonDatePicker.module.scss';

type RangeValue = { startDate: string; endDate: string };

type CommonProps = {
  label?: string;
  required?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  height?: number | string;
  width?: number | string;
  maxDate?: Date;
  minDate?: Date;
};

type RangeProps = CommonProps & {
  mode: 'rangeDate';
  value?: RangeValue;
  onChange?: (date: RangeValue) => void;
};

type DateProps = CommonProps & {
  mode?: 'date';
  value?: string | Date;
  onChange?: (date: string) => void;
};

type TimeProps = CommonProps & {
  mode: 'time';
  value?: string;
  onChange?: (date: string) => void;
};

type Props = RangeProps | DateProps | TimeProps;

type TextFieldInputProps = InputHTMLAttributes<HTMLInputElement>;

const cx = classNames.bind(styles);

const CommonDatePicker = (props: Props) => {
  //#region Destructuring Props
  const {
    label,
    errorMessage,
    disabled = false,
    required = false,
    height = 35,
    width,
    minDate,
    maxDate,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [rangeDate, setRangeDate] = useState<[Date | null, Date | null]>([null, null]);
  const [dateText, setDateText] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!props.value) {
      setSelectedDate(null);
      setDateText(EMPTY_STRING);
      return;
    }

    if (props.mode === 'rangeDate') {
      handleValueRangeDate(props.value);
    }

    if (props.mode === 'date') {
      handleValueDate(props.value as string);
      return;
    }

    if (props.mode === 'time') {
      handleValueTime(props.value as string);
      return;
    }
  }, [props.value, props.mode]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleValueDate = (value: string) => {
    const parsed = moment(value);
    if (!parsed.isValid()) {
      setSelectedDate(null);
      setDateText(EMPTY_STRING);
      return;
    }
    const date = parsed.toDate();
    const dateString = parsed.format(DateFormatEnum.DD_MMM_YYYY);
    setSelectedDate(date);
    setDateText(dateString);
  };

  const handleValueTime = (time: string) => {
    const today = moment().format(DateFormatEnum.YYYY_MM_DD);
    const dateTime = moment(`${today} ${time}`, `${DateFormatEnum.YYYY_MM_DD} ${DateFormatEnum.HH_MM}`);
    if (!dateTime.isValid()) {
      setSelectedDate(null);
      setDateText(EMPTY_STRING);
      return;
    }
    const date = dateTime.toDate();
    const dateString = dateTime.format(DateFormatEnum.HH_MM_A);
    setSelectedDate(date);
    setDateText(dateString);
  };

  const handleValueRangeDate = (values: RangeValue) => {
    const { startDate, endDate } = (values ?? {}) as { startDate?: string; endDate?: string };
    const start = startDate ? moment(startDate).toDate() : null;
    const end = endDate ? moment(endDate).toDate() : null;
    setRangeDate([start, end]);

    if (start && end) {
      setDateText(
        `${moment(start).format(DateFormatEnum.DD_MMM_YYYY)} - ${moment(end).format(
          DateFormatEnum.DD_MMM_YYYY
        )}`
      );
    } else if (start) {
      setDateText(moment(start).format(DateFormatEnum.DD_MMM_YYYY));
    } else if (end) {
      setDateText(moment(end).format(DateFormatEnum.DD_MMM_YYYY));
    } else {
      setDateText(EMPTY_STRING);
    }
  };

  const handleChange = (date: Date | null) => {
    if (!date) return;

    setSelectedDate(date);
    if (props.mode === 'time' || props.mode === 'date') {
      props.onChange &&
        props.onChange(
          moment(date).format(props.mode === 'time' ? DateFormatEnum.HH_MM : DateFormatEnum.YYYY_MM_DD)
        );
    }

    setDateText(
      moment(date).format(props.mode === 'time' ? DateFormatEnum.HH_MM_A : DateFormatEnum.MM_DD_YYYY)
    );
  };

  const handleChangeRange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setRangeDate(dates);

    if (start && end && props.mode === 'rangeDate') {
      const startDate = moment(start).format(DateFormatEnum.YYYY_MM_DD);
      const endDate = moment(end).format(DateFormatEnum.YYYY_MM_DD);
      setDateText(
        `${moment(start).format(DateFormatEnum.MM_DD_YYYY)} - ${moment(end).format(
          DateFormatEnum.MM_DD_YYYY
        )}`
      );
      props.onChange?.({ startDate, endDate });
    }
  };

  const TextFieldInput = forwardRef<HTMLInputElement, TextFieldInputProps>(({ onClick }, ref) => (
    <TextField
      fullWidth
      ref={ref}
      classes={{ root: cx('textFieldDatePicker') }}
      slotProps={{
        input: {
          className: cx('textFieldInput', { disabled: disabled }, { error: !!errorMessage }),
          sx: {
            height,
            width,
          },
          readOnly: true,
          endAdornment: (
            <InputAdornment position='end'>
              <img
                src={props.mode === 'time' ? icons.commonIconClock : icons.commonIconCalendar}
                alt={t('common_alt_img', { ns: 'common/base' })}
                className={cx('iconInput')}
              />
            </InputAdornment>
          ),
        },
      }}
      disabled={disabled}
      onClick={onClick}
      value={dateText}
      placeholder={t('common_select_placeholder')}
      error={!!errorMessage}
      helperText={
        errorMessage && <FormHelperText className={cx('errorMessage')}>{errorMessage}</FormHelperText>
      }
    />
  ));
  //#endregion Handle Function

  return (
    <Box component='div' id='commonDatepicker' className={cx('container')} sx={{ width }}>
      {label && (
        <InputLabel required={required} className={cx('label')}>
          {label}
        </InputLabel>
      )}

      <DatePicker
        {...(props.mode === 'rangeDate'
          ? {
              selectsRange: true,
              startDate: rangeDate[0] ?? undefined,
              endDate: rangeDate[1] ?? undefined,
              onChange: handleChangeRange,
            }
          : {
              selected: selectedDate,
              onChange: handleChange,
            })}
        portalId='baseDatePicker'
        popperContainer={Portal}
        popperPlacement='bottom-start'
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        showTimeSelectOnly={props.mode === 'time'}
        showTimeSelect={props.mode === 'time'}
        timeIntervals={props.mode === 'time' ? 30 : undefined}
        showTimeCaption={false}
        showPopperArrow={false}
        dateFormat={props.mode === 'time' ? DateFormatEnum.HH_MM : DateFormatEnum.YYYY_MM_DD}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <HeaderDatePicker
            date={date}
            onChangeYear={changeYear}
            onChangeMonth={changeMonth}
            decreaseMonth={decreaseMonth}
            increaseMonth={increaseMonth}
            prevMonthButtonDisabled={prevMonthButtonDisabled}
            nextMonthButtonDisabled={nextMonthButtonDisabled}
          />
        )}
        customInput={<TextFieldInput />}
      />
    </Box>
  );
};

export default memo(CommonDatePicker);
